import { OrderItemStatus, OrderItemType, OrderStatus } from '@rd/types';
import { z } from 'zod';
import { cashBackModelSchema } from './membership.schema';

/***
 * Order Service Interfaces
 * ***/
export const nydusAddressSchema = z.object({
  city: z.string().nullable(),
  countryCode: z.string().nullable(),
  line_1: z.string().nullable(),
  line_2: z.string().nullable(),
  full_address: z.string().nullable(),
  postalCode: z.string().nullable(),
  state: z.string().nullable(),
});

export const nydusRecipientSchema = z.object({
  address: nydusAddressSchema.nullable(),
  companyName: z.string().nullable(),
  lastName: z.string(),
  firstName: z.string(),
  phoneNumber: z.string().nullable(),
  email: z.string().nullable(),
});

/***
 * Order Item Data
 * ***/
export const orderAddressSchema = z.object({
  city: z.string().nullable(),
  countryCode: z.string().nullable(),
  line1: z.string().nullable(),
  line2: z.string().nullable(),
  postalCode: z.string().nullable(),
  state: z.string().nullable(),
});

export const baseGiftCardOrderItemDataSchema = z.object({
  amount: z.number(),
  cardId: z.string(),
  currency: z.string(),
  productName: z.string(),
  productImageUrl: z.string(),
  supplier: z.string(),
  displayValue: z.number(),
  supplierReferenceId: z.string(),
  recipient: z.object({
    address: orderAddressSchema.nullable(),
    companyName: z.string(),
    message: z.string(),
    lastName: z.string(),
    firstName: z.string(),
    phoneNumber: z.string(),
    emailAddress: z.string(),
  }),
  fulfillmentUrl: z.string(),
});

export const digitalGiftCardFulfillmentDataSchema = z.object({
  downloadUrl: z.string(),
  fulfillmentUrl: z.string(),
  receiptCode: z.string(),
  redemptionUrl: z.string(),
  viewUrl: z.string(),
});

export const physicalGiftCardFulfillmentDataSchema = z.object({
  shippedStatus: z.string(),
  trackingId: z.string(),
  trackingUrl: z.string(),
});

export const giftCardOrderItemDataSchema =
  baseGiftCardOrderItemDataSchema.extend({
    fulfillmentData: digitalGiftCardFulfillmentDataSchema,
  });

export const physicalGiftCardOrderItemDataSchema =
  baseGiftCardOrderItemDataSchema.extend({
    fulfillmentData: physicalGiftCardFulfillmentDataSchema,
  });

export const cashRedemptionOrderItemDataSchema = z.object({
  amount: z.number(),
  amountInCents: z.number(),
  currency: z.string(),
  currencyShortName: z.string(),
  loyaltyProgramId: z.string(),
  membership: cashBackModelSchema.optional(),
  partnerUserId: z.string(),
  programShortName: z.string(),
  tenantCurrencyShortName: z.string(),
});

export const merchandiseOrderItemDataSchema = z.object({
  amount: z.number(),
  cashAmount: z.number(),
  currency: z.string(),
  displayValue: z.number(),
  externalSupplierOrderReference: z.string(),
  fulfillmentData: physicalGiftCardFulfillmentDataSchema,
  merchandiseId: z.string(),
  orderSourceType: z.string(),
  pointsPercentage: z.number(),
  productImageUrl: z.string(),
  productName: z.string(),
  quantity: z.number(),
  recipient: nydusRecipientSchema,
  sku: z.string(),
  supplier: z.string(),
});

const membershipInformationSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  number: z.string(),
});

export const pointsTransferOrderItemMetadataSchema = z.object({
  loyaltyCurrencyName: z.string(),
  loyaltyCurrencyShortName: z.string(),
  loyaltyProgramId: z.string(),
  membershipInformation: membershipInformationSchema,
  productName: z.string(),
  programShortName: z.string(),
  tenantCurrencyName: z.string(),
  tenantCurrencyShortName: z.string(),
  transferAmount: z.number(),
});

export const orderItemDataSchema = z.union([
  cashRedemptionOrderItemDataSchema,
  giftCardOrderItemDataSchema,
  physicalGiftCardOrderItemDataSchema,
  merchandiseOrderItemDataSchema,
  pointsTransferOrderItemMetadataSchema,
]);

/**
 * Notification
 */
export const notificationChannelSchema = z.enum([
  'ascenda_email',
  'partner_email',
  'partner_sms',
  'partner_push',
  'partner_widget',
  'supplier_mms',
]);

export const notificationSchema = z.object({
  id: z.string(),
  type: z.literal('notification'),
  channel: notificationChannelSchema,
  eventName: z.string(),
});

/***
 * Order Item Type
 * ***/
export const orderItemAttributesSchema = z.object({
  userId: z.string(),
  status: z.nativeEnum(OrderItemStatus),
  description: z.string(),
  quantity: z.number(),
  cashPaid: z.number(),
  cashAmount: z.number(),
  pointsPaid: z.number(),
  pointsAmount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
  data: orderItemDataSchema,
});

export const orderItemBaseSchema = z.object({
  id: z.string(),
  type: z.nativeEnum(OrderItemType),
  attributes: orderItemAttributesSchema.optional(),
  notifications: z.array(notificationSchema),
});

export const giftCardOrderItemAttributesSchema =
  orderItemAttributesSchema.extend({ data: giftCardOrderItemDataSchema });

export const giftCardOrderItemSchema = orderItemBaseSchema.extend({
  type: z.literal(OrderItemType.GiftCardOrderItem),
  attributes: giftCardOrderItemAttributesSchema,
});

export const physicalGiftCardOrderItemAttributesSchema =
  orderItemAttributesSchema.extend({
    data: physicalGiftCardOrderItemDataSchema,
  });

export const physicalGiftCardOrderItemSchema = orderItemBaseSchema.extend({
  type: z.literal(OrderItemType.PhysicalGiftCardOrderItem),
  attributes: physicalGiftCardOrderItemAttributesSchema,
});

export const cashRedemptionOrderItemAttributesSchema =
  orderItemAttributesSchema.extend({
    data: cashRedemptionOrderItemDataSchema,
  });

export const cashRedemptionOrderItemSchema = orderItemBaseSchema.extend({
  type: z.literal(OrderItemType.CashRedemptionOrderItem),
  attributes: cashRedemptionOrderItemAttributesSchema,
});

export const pointsTransferOrderItemAttributesSchema =
  orderItemAttributesSchema.extend({
    data: pointsTransferOrderItemMetadataSchema,
  });

export const pointsTransferOrderItemSchema = orderItemBaseSchema.extend({
  type: z.literal(OrderItemType.PointsTransferOrderItem),
  attributes: pointsTransferOrderItemMetadataSchema,
});

export const merchandiseOrderItemAttributesSchema =
  orderItemAttributesSchema.extend({
    data: merchandiseOrderItemDataSchema,
  });

export const merchandiseOrderItemSchema = orderItemBaseSchema.extend({
  type: z.literal(OrderItemType.MerchandiseOrderItem),
  attributes: merchandiseOrderItemAttributesSchema,
});

export const voucherOrderItemAttributesSchema =
  orderItemAttributesSchema.extend({
    data: pointsTransferOrderItemMetadataSchema,
  });

export const voucherOrderItemSchema = orderItemBaseSchema.extend({
  type: z.literal(OrderItemType.VoucherOrderItem),
  attributes: voucherOrderItemAttributesSchema,
});

export const orderItemSchema = z.discriminatedUnion('type', [
  giftCardOrderItemSchema,
  physicalGiftCardOrderItemSchema,
  cashRedemptionOrderItemSchema,
  pointsTransferOrderItemSchema,
  merchandiseOrderItemSchema,
  voucherOrderItemSchema,
]);

export const orderSchema = z.object({
  id: z.string(),
  type: z.literal('order'),
  userId: z.string(),
  status: z.nativeEnum(OrderStatus),
  fulfillmentStatus: z.nativeEnum(OrderItemStatus),
  stripePaymentIntentClientSecret: z.string().nullable(),
  cashPaid: z.number(),
  cashAmount: z.number(),
  pointsAmount: z.number(),
  pointsPaid: z.number(),
  data: z.object({
    recipient: nydusRecipientSchema.optional(),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
  orderItems: z.array(orderItemSchema),
});

export const orderModelRelationshipsOrderItem = z.object({
  data: z.object({
    id: z.string(),
    type: z.nativeEnum(OrderItemType),
  }),
});

export const orderModelSchema = z.object({
  id: z.string(),
  type: z.literal('order'),
  attributes: orderSchema.omit({ id: true, type: true }),
  relationships: z.object({
    orderItem: z.union([
      orderModelRelationshipsOrderItem,
      z.array(orderModelRelationshipsOrderItem),
    ]),
  }),
});

/***
 * Order Item Model
 * ***/
export const baseOrderItemModelSchema = z.object({
  id: z.string(),
  relationships: z.object({
    order: z.object({
      data: z.object({
        id: z.string(),
        type: z.literal('order'),
      }),
    }),
  }),
});

export const baseOrderItemModelAttributesSchema = z.object({
  userId: z.string(),
  description: z.string(),
  status: z.nativeEnum(OrderItemStatus),
  quantity: z.number(),
  cashPaid: z.number(),
  cashAmount: z.number(),
  pointsAmount: z.number(),
  pointsPaid: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const giftCardOrderItemModelAttributesSchema =
  baseOrderItemModelAttributesSchema.extend({
    data: giftCardOrderItemDataSchema,
  });

export const physicalGiftCardOrderItemModelAttributesSchema =
  baseOrderItemModelAttributesSchema.extend({
    data: physicalGiftCardOrderItemDataSchema,
  });

export const cashRedemptionOrderItemModelAttributesSchema =
  baseOrderItemModelAttributesSchema.extend({
    data: cashRedemptionOrderItemDataSchema,
  });

export const pointsTransferOrderItemModelAttributesSchema =
  baseOrderItemModelAttributesSchema.extend({
    data: pointsTransferOrderItemMetadataSchema,
  });

export const merchandiseOrderItemModelAttributesSchema =
  baseOrderItemModelAttributesSchema.extend({
    data: merchandiseOrderItemDataSchema,
  });

export const giftCardOrderItemModelSchema = baseOrderItemModelSchema.extend({
  type: z.literal(OrderItemType.GiftCardOrderItem),
  attributes: giftCardOrderItemAttributesSchema,
});

export const physicalGiftCardOrderItemModelSchema =
  baseOrderItemModelSchema.extend({
    type: z.literal(OrderItemType.PhysicalGiftCardOrderItem),
    attributes: physicalGiftCardOrderItemAttributesSchema,
  });

export const cashRedemptionOrderItemModelSchema =
  baseOrderItemModelSchema.extend({
    type: z.literal(OrderItemType.CashRedemptionOrderItem),
    attributes: cashRedemptionOrderItemAttributesSchema,
  });

export const pointsTransferOrderItemModelSchema =
  baseOrderItemModelSchema.extend({
    type: z.literal(OrderItemType.PointsTransferOrderItem),
    attributes: pointsTransferOrderItemMetadataSchema,
  });

export const merchandiseOrderItemModelSchema = baseOrderItemModelSchema.extend({
  type: z.literal(OrderItemType.MerchandiseOrderItem),
  attributes: merchandiseOrderItemAttributesSchema,
});

export const voucherOrderItemModelSchema = baseOrderItemModelSchema.extend({
  type: z.literal(OrderItemType.VoucherOrderItem),
  attributes: voucherOrderItemAttributesSchema,
});

export const orderItemModelSchema = z.discriminatedUnion('type', [
  giftCardOrderItemModelSchema,
  physicalGiftCardOrderItemModelSchema,
  cashRedemptionOrderItemModelSchema,
  pointsTransferOrderItemModelSchema,
  merchandiseOrderItemModelSchema,
  voucherOrderItemModelSchema,
]);

/***
 * Notification Model
 */
export const notificationModelSchema = z.object({
  id: z.string(),
  type: z.literal('notification'),
  attributes: notificationSchema.omit({ id: true, type: true }),
  relationships: z.object({
    notificationTopic: z.object({
      data: z.object({
        id: z.string(),
        type: z.nativeEnum(OrderItemType),
      }),
    }),
  }),
});

export const ordersResponseSchema = z.object({
  data: z.array(orderModelSchema),
  meta: z.object({
    count: z.number(),
  }),
  included: z.array(z.union([orderItemModelSchema, notificationModelSchema])),
});

export const showOrderResponseSchema = z.object({
  data: orderModelSchema,
  included: z.array(orderItemModelSchema),
});

export const postOrderResponseSchema = z.object({
  data: orderModelSchema,
  included: z.array(orderItemModelSchema),
});

export const updateOrderResponseSchema = postOrderResponseSchema.omit({
  included: true,
});
